import { default as React, useState, useMemo, useCallback } from 'react';
import {
  Grid,
  Typography,
  Box,
  InputBase,
  IconButton,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VpnLockIcon from '@material-ui/icons/VpnLock';
import LockIcon from '@material-ui/icons/Lock';
import { Entity, RELATION_OWNED_BY } from '@backstage/catalog-model';
import {
  useEntityList,
  entityRouteRef,
  entityRouteParams,
} from '@backstage/plugin-catalog-react';
import { useRouteRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { UnregisterAppButton } from './UnregisterAppButton';
import {
  useCurrentUserEmail,
  isUserAuthorizedForEntity,
  isUserAllowedToAccessEntity,
  useSkyStageAdminEmails,
  useMicroappLaunchUrl,
  isAccessAppColumn,
} from './hooks';
import { AccessDeniedModal } from './AccessDeniedModal';

const CARDS_PER_PAGE = 20;

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    width: 280,
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  },
  searchInput: {
    flex: 1,
    fontSize: '0.875rem',
  },
  clearButton: {
    padding: 4,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.2s ease-in-out',
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    },
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2.5),
    '&:last-child': {
      paddingBottom: theme.spacing(2.5),
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
  },
  name: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameLink: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: '#1976d2',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  vpnBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    color: '#228189',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    padding: '3px 6px',
    backgroundColor: 'rgba(34, 129, 137, 0.1)',
    borderRadius: '4px',
  },
  vpnIcon: {
    color: '#228189',
    fontSize: '12px',
    flexShrink: 0,
  },
  vpnText: {
    fontSize: '0.65rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    letterSpacing: '0.3px',
    textTransform: 'uppercase' as const,
    lineHeight: 1,
  },
  externalIcon: {
    padding: 4,
    color: theme.palette.text.secondary,
    opacity: 0.5,
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
  },
  description: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: 1.5,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: `${theme.spacing(0.25)}px ${theme.spacing(2)}px`,
    marginBottom: theme.spacing(1.5),
    fontSize: '0.825rem',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    overflow: 'hidden',
  },
  metaLabel: {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  metaValue: {
    fontSize: '0.825rem',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaLink: {
    fontSize: '0.825rem',
    color: '#1976d2',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  customColumnsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: 'auto',
  },
  accessButton: {
    textTransform: 'none',
    fontSize: '0.75rem',
    padding: '4px 8px',
    minWidth: 'auto',
  },
  restrictedCard: {
    opacity: 0.5,
    cursor: 'default',
  },
  lockBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    color: '#e65100',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    padding: '3px 6px',
    backgroundColor: 'rgba(230, 81, 0, 0.1)',
    borderRadius: '4px',
  },
  lockIcon: {
    color: '#e65100',
    fontSize: '12px',
    flexShrink: 0,
  },
  lockText: {
    fontSize: '0.65rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    letterSpacing: '0.3px',
    textTransform: 'uppercase' as const,
    lineHeight: 1,
  },
  manageButton: {
    marginLeft: theme.spacing(1),
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'none' as const,
    borderRadius: 4,
  },
}));

function getOwnerTargetRef(entity: Entity): string | null {
  const ownerRelation = entity.relations?.find(
    r => r.type === RELATION_OWNED_BY,
  );
  return ownerRelation?.targetRef || null;
}

function getDisplayName(targetRef: string): string {
  return targetRef.split('/').pop() || targetRef;
}

interface CustomColumnDef {
  title: string;
  type: string;
  value?: string;
  linkText?: string;
}

function parseCustomColumns(entity: Entity): CustomColumnDef[] {
  const annotation = entity.metadata.annotations?.['backstage.io/columns'];
  if (!annotation) return [];
  try {
    const cleaned = annotation
      .replace(/\n\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return JSON.parse(cleaned);
  } catch {
    return [];
  }
}

function entityMatchesSearch(entity: Entity, term: string): boolean {
  const lower = term.toLowerCase();
  const name = (
    entity.metadata.title ||
    entity.metadata.name ||
    ''
  ).toLowerCase();
  const description = (entity.metadata.description || '').toLowerCase();
  const owner = (
    entity.relations?.find(r => r.type === RELATION_OWNED_BY)?.targetRef || ''
  ).toLowerCase();

  return (
    name.includes(lower) || description.includes(lower) || owner.includes(lower)
  );
}

const MicroappCard = ({
  entity,
  onClick,
  currentUserEmail,
  onDeleteSuccess,
  onRestrictedClick,
}: {
  entity: Entity;
  onClick?: (entity: Entity) => void;
  currentUserEmail: string;
  onDeleteSuccess: () => void;
  onRestrictedClick: (entity: Entity) => void;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  const adminEmails = useSkyStageAdminEmails();

  const name = entity.metadata.title || entity.metadata.name;
  const description = entity.metadata.description || '';
  // Production URL when the app has been promoted (the entity carries the
  // staging URL); otherwise the entity's own URL.
  const viewUrl = entity.metadata.annotations?.['backstage.io/view-url'];
  const appUrl = useMicroappLaunchUrl(entity, viewUrl);
  // Set only when production replaced the entity URL.
  const productionUrl = appUrl !== viewUrl ? appUrl : undefined;
  const requiresVPN =
    entity.metadata.annotations?.['microapp/requiresVPN'] === 'true';
  const ownerTargetRef = getOwnerTargetRef(entity);
  const customColumns = parseCustomColumns(entity);
  const isAuthorized = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );
  const isAllowed = isUserAllowedToAccessEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );
  const isRestricted =
    entity.metadata.annotations?.['microapp/visibility'] ===
    'Custom (Specific Users)';

  const ownerLink = ownerTargetRef
    ? entityRoute(entityRouteParams(ownerTargetRef))
    : null;

  const handleCardClick = () => {
    if (!isAllowed) {
      onRestrictedClick(entity);
      return;
    }
    if (onClick) onClick(entity);
  };

  const cardClassName = isAllowed
    ? classes.card
    : `${classes.card} ${classes.restrictedCard}`;

  return (
    <Card
      className={cardClassName}
      variant="outlined"
      onClick={handleCardClick}
    >
      <CardContent className={classes.cardContent}>
        {/* Header: Name + VPN + Lock + action icons */}
        <div className={classes.cardHeader}>
          <div className={classes.nameRow}>
            {appUrl && isAllowed ? (
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.nameLink}
                onClick={e => e.stopPropagation()}
              >
                {name}
              </a>
            ) : (
              <Typography className={classes.name}>{name}</Typography>
            )}
            {isRestricted && (
              <Tooltip title="Access to this app is restricted to specific users.">
                <div className={classes.lockBadge}>
                  <LockIcon
                    className={classes.lockIcon}
                    aria-label="Restricted access"
                  />
                  <span className={classes.lockText}>Restricted</span>
                </div>
              </Tooltip>
            )}
            {requiresVPN && (
              <Tooltip title="This app requires VPN connection to access.">
                <div className={classes.vpnBadge}>
                  <VpnLockIcon className={classes.vpnIcon} />
                  <span className={classes.vpnText}>VPN</span>
                </div>
              </Tooltip>
            )}
          </div>
          <div className={classes.cardActions}>
            {isAuthorized && (
              <UnregisterAppButton
                entity={entity}
                iconOnly
                onDeleted={onDeleteSuccess}
              />
            )}
          </div>
        </div>

        {/* Description */}
        {description && (
          <Typography className={classes.description}>{description}</Typography>
        )}

        {/* Metadata: Owner */}
        <div className={classes.metaGrid}>
          <div className={classes.metaItem}>
            <span className={classes.metaLabel}>Owner:</span>
            {ownerLink && ownerTargetRef ? (
              <Link
                to={ownerLink}
                className={classes.metaLink}
                onClick={e => e.stopPropagation()}
              >
                {getDisplayName(ownerTargetRef)}
              </Link>
            ) : (
              <span className={classes.metaValue}>-</span>
            )}
          </div>
        </div>

        {/* Custom columns data (Access App buttons, dates, etc.) */}
        {customColumns.length > 0 && (
          <div className={classes.customColumnsRow}>
            {customColumns.map((col, idx) => {
              if (!col.value) return null;
              if (col.type === 'url') {
                if (!isAllowed) {
                  return (
                    <Button
                      key={idx}
                      variant="contained"
                      size="small"
                      className={classes.accessButton}
                      disabled
                      aria-label="Access restricted"
                    >
                      Access Restricted
                    </Button>
                  );
                }
                return (
                  <Button
                    key={idx}
                    variant="contained"
                    color="primary"
                    size="small"
                    href={
                      isAccessAppColumn(col.title) && productionUrl
                        ? productionUrl
                        : col.value
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.accessButton}
                    onClick={e => e.stopPropagation()}
                  >
                    {col.linkText || 'Access App'}
                  </Button>
                );
              }
              if (col.type === 'date') {
                try {
                  const date = new Date(col.value);
                  return (
                    <Chip
                      key={idx}
                      label={`${col.title}: ${date.toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}`}
                      size="small"
                      variant="outlined"
                      style={{ height: 22, fontSize: '0.72rem' }}
                    />
                  );
                } catch {
                  return null;
                }
              }
              return (
                <Chip
                  key={idx}
                  label={`${col.title}: ${col.value}`}
                  size="small"
                  variant="outlined"
                  style={{ height: 22, fontSize: '0.72rem' }}
                />
              );
            })}
            {isAuthorized && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  navigate(entityRoute(entityRouteParams(entity)));
                }}
                className={classes.manageButton}
                aria-label="Manage App"
              >
                Manage App
              </Button>
            )}
          </div>
        )}

        {/* Fallback Access App button from view-url */}
        {customColumns.length === 0 && appUrl && (
          <div className={classes.customColumnsRow}>
            {isAllowed ? (
              <Button
                variant="contained"
                color="primary"
                size="small"
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.accessButton}
                onClick={e => e.stopPropagation()}
              >
                Access App
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                className={classes.accessButton}
                disabled
                aria-label="Access restricted"
              >
                Access Restricted
              </Button>
            )}
            {isAuthorized && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  navigate(entityRoute(entityRouteParams(entity)));
                }}
                className={classes.manageButton}
                aria-label="Manage App"
              >
                Manage App
              </Button>
            )}
          </div>
        )}

        {/* Manage App button for apps with no access button */}
        {customColumns.length === 0 && !appUrl && isAuthorized && (
          <div className={classes.customColumnsRow}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={e => {
                e.stopPropagation();
                navigate(entityRoute(entityRouteParams(entity)));
              }}
              className={classes.manageButton}
              aria-label="Manage App"
            >
              Manage App
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const MicroappCardGrid = () => {
  const classes = useStyles();
  const { entities, updateFilters } = useEntityList();
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  const currentUserEmail = useCurrentUserEmail();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [deniedEntity, setDeniedEntity] = useState<Entity | null>(null);

  const filteredEntities = useMemo(() => {
    if (!searchTerm.trim()) return entities;
    return entities.filter(e => entityMatchesSearch(e, searchTerm.trim()));
  }, [entities, searchTerm]);

  const totalPages = Math.ceil(filteredEntities.length / CARDS_PER_PAGE);
  const paginatedEntities = useMemo(() => {
    const start = (page - 1) * CARDS_PER_PAGE;
    return filteredEntities.slice(start, start + CARDS_PER_PAGE);
  }, [filteredEntities, page]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleCardClick = (entity: Entity) => {
    navigate(entityRoute(entityRouteParams(entity)));
  };

  const handleDeleteSuccess = useCallback(() => {
    // Trigger an entity list refresh by toggling filters
    // This preserves the current search term and page while refreshing data
    // The deleted entity will be excluded on the next catalog fetch
    updateFilters({ _refresh: Date.now() } as any);
    // Adjust page if current page would be empty after deletion
    setPage(prevPage => {
      const newTotal = Math.ceil(
        (filteredEntities.length - 1) / CARDS_PER_PAGE,
      );
      return prevPage > newTotal && newTotal > 0 ? newTotal : prevPage;
    });
  }, [updateFilters, filteredEntities.length]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="h6">
          {`All Apps (${filteredEntities.length})`}
        </Typography>
        <Paper elevation={0} className={classes.searchBox}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            placeholder="Filter"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
          />
          {searchTerm && (
            <IconButton
              size="small"
              className={classes.clearButton}
              onClick={() => handleSearchChange('')}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Paper>
      </div>

      {filteredEntities.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography variant="h6">No apps found</Typography>
          <Typography variant="body2">
            Try adjusting your search or filters.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {paginatedEntities.map(entity => {
              const key = `${entity.metadata.namespace || 'default'}/${
                entity.kind
              }/${entity.metadata.name}`;
              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <MicroappCard
                    entity={entity}
                    onClick={handleCardClick}
                    currentUserEmail={currentUserEmail}
                    onDeleteSuccess={handleDeleteSuccess}
                    onRestrictedClick={setDeniedEntity}
                  />
                </Grid>
              );
            })}
          </Grid>

          {totalPages > 1 && (
            <div className={classes.paginationContainer}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
              />
            </div>
          )}
        </>
      )}

      <AccessDeniedModal
        entity={deniedEntity}
        open={!!deniedEntity}
        onClose={() => setDeniedEntity(null)}
      />
    </div>
  );
};
